import { trackEvent } from 'services/Gtm/functions';
import type { EventData } from 'services/Gtm/entities';
import type { ButtonSizeStyling } from 'components/Globals/Base/Button/types';
import type { AnchorHTMLAttributes, DetailedHTMLProps } from 'react';
import cx from 'classnames';
import type CustomWindow from 'interfaces/Utils';
import type { LinkTypes } from './types';
import * as S from './LinkStyles';

declare let window: CustomWindow;

export interface CustomLinkProps extends DetailedHTMLProps<AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement> {
  dataCy?: string;
  trackingData?: EventData;
  // TODO: the ariaLabel prop can be removed and sent directly through aria-label
  ariaLabel?: string;
  type?: keyof typeof LinkTypes;
  size?: { default: keyof ButtonSizeStyling; [key: string]: keyof ButtonSizeStyling };
  center?: boolean;
  hasRightArrow?: boolean;
  hasLeftArrow?: boolean;
  onKeyDown?: () => void;
  onMouseEnter?: () => void;
}

// make full reloads when changing a route, in order to disable SPA
export const changeRoute = (url: string) => {
  window.location.href = url;
  return Promise.resolve(true);
};

export const parseHref = (href?: string) => {
  if (!href) {
    return '';
  }

  const currentEnvironment = process.env.ENVIRONMENT;
  if (currentEnvironment === 'prod' && href.startsWith('/')) {
    return `https://fortune.com${href}`;
  }

  return href;
};

/**
 * Wrapper component around next/link that allows passing a className (which is often needed on links).
 * This component also handles tracking on clicks.
 */
const CustomLink = ({
  href,
  target = '_self',
  ariaLabel = '',
  children,
  className = '',
  dataCy,
  trackingData,
  type,
  size,
  center,
  hasRightArrow = true,
  hasLeftArrow = false,
  onClick: parentOnClick,
  ...rest
}: CustomLinkProps) => {
  const parsedHref = parseHref(href);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (trackEvent && trackingData) {
      setTimeout(() => trackEvent(trackingData), 5);
    }
    parentOnClick?.(e);
  };

  const securityProps = target === '_blank' ? { rel: 'noopener noreferrer' } : {};

  return (
    <S.Link
      $type={type || 'primary'}
      $size={size}
      $center={center}
      $hasRightArrow={hasRightArrow}
      $hasLeftArrow={hasLeftArrow}
      href={parsedHref}
      target={target}
      className={cx(className, { 'styled-custom-link': type })}
      data-cy={dataCy}
      onClick={handleClick}
      {...securityProps}
      {...rest}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      suppressHydrationWarning
    >
      {children}
    </S.Link>
  );
};

export default CustomLink;
